<template>
  <div class="home">
    <header-el></header-el>

    <!-- Hero：品牌红大色块（参考官网首屏） -->
    <section class="hero">
      <div class="ui-container hero__wrap">
        <div class="hero__text">
          <span class="hero__badge">AIoT 全链路技术服务商</span>
          <h1>连接万物 · 融智于云<br />共创数智未来</h1>
          <p class="hero__desc">
            （ASSTAR）专注于物联网、人工智能、AR 交互及智能硬件定制，
            打通「硬件—云端—应用—生态」闭环，提供从研发到落地的一体化数智化解决方案。
          </p>
          <div class="hero__actions">
            <router-link class="btn btn--red" to="/products.html">了解产品与服务</router-link>
            <router-link class="btn btn--white" to="/solutions.html">解决方案</router-link>
          </div>
        </div>

        <div class="hero__visual">
          <div class="hero-card hero-card--main">
            <div class="hero-card__hd">
              <span class="hero-card__dot"></span>
              设备在线 · 实时管控
            </div>
            <div class="hero-card__rows">
              <div class="hero-row" v-for="d in heroDevices" :key="d.name">
                <span class="hero-row__name">{{ d.name }}</span>
                <span class="hero-row__meta">{{ d.meta }}</span>
                <span class="hero-row__ok">{{ d.ok }}</span>
              </div>
            </div>
          </div>
          <div class="hero-card hero-card--sub">
            <b>AI 大模型中台</b>
            <p>知识库问答 · 智能体工作流</p>
          </div>
        </div>
      </div>

      <!-- 悬浮白卡：数据指标 -->
      <div class="ui-container stats-wrap">
        <div class="stats card">
          <div class="stats__item" v-for="s in homeStats" :key="s.label">
            <b>{{ s.num }}</b>
            <span>{{ s.label }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- 核心业务板块 -->
    <section class="section">
      <div class="ui-container">
        <div class="section-head">
          <span class="kicker">CORE BUSINESS</span>
          <h2>核心业务板块</h2>
          <p class="sub">物联网生态、AI 软件与 ITO 全栈服务</p>
        </div>
        <div class="pillars">
          <div class="pillar card" v-for="p in solutionPillars" :key="p.name">
            <div class="pillar__pic">
              <img :src="p.image" :alt="p.name" />
            </div>
            <div class="pillar__body">
              <h3>{{ p.name }}</h3>
              <p>{{ p.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 产品速览 -->
    <section class="section section--gray">
      <div class="ui-container">
        <div class="section-head">
          <span class="kicker">PRODUCTS</span>
          <h2>AIoT 智能产品</h2>
          <p class="sub">AI 伙伴 · 智能教育 · 智能家居 · 宠物生活 · 行业终端，全部支持在线固件升级</p>
        </div>
        <div class="prod-grid">
          <div class="prod card" v-for="p in featuredProducts" :key="p.name">
            <span class="tag" v-if="p.tag">{{ p.tag }}</span>
            <div class="prod__pic">
              <img :src="p.image" :alt="p.name" />
            </div>
            <h3>{{ p.name }}</h3>
            <p>{{ p.desc }}</p>
            <span class="prod__fw" v-if="p.firmware > 0">{{ p.firmware }} 个固件 · 在线升级</span>
          </div>
        </div>
        <div class="more-line">
          <router-link class="btn btn--ghost" to="/products.html">查看全部产品</router-link>
        </div>
      </div>
    </section>

    <!-- 服务双卡 -->
    <section class="section">
      <div class="ui-container">
        <div class="section-head">
          <span class="kicker">SERVICES</span>
          <h2>产品与服务</h2>
          <p class="sub">AIOT 服务 × 大模型服务，双轮驱动产业智能化</p>
        </div>
        <div class="svc-grid">
          <div class="svc card" v-for="s in serviceCards" :key="s.name">
            <div class="svc__pic">
              <img :src="s.image" :alt="s.name" />
            </div>
            <div class="svc__body">
              <span class="svc__en">{{ s.en }}</span>
              <h3>{{ s.name }}</h3>
              <p class="svc__desc">{{ s.desc }}</p>
              <ul class="check-list">
                <li v-for="pt in s.points" :key="pt">{{ pt }}</li>
              </ul>
              <router-link class="btn btn--primary btn--sm svc__btn" :to="s.to">了解详情</router-link>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 技术底座 -->
    <section class="section section--gray">
      <div class="ui-container">
        <div class="section-head">
          <span class="kicker">TECHNOLOGY</span>
          <h2>自研技术底座</h2>
          <p class="sub">敏捷开发，快速迭代，为 AIoT 应用高效落地提供强大支撑</p>
        </div>
        <div class="tech-grid">
          <div class="tech card" v-for="t in techPlatforms" :key="t.name">
            <div class="tech__pic">
              <img :src="t.image" :alt="t.name" />
            </div>
            <h3>{{ t.name }}</h3>
            <p>{{ t.desc }}</p>
          </div>
        </div>
        <div class="more-line">
          <router-link class="btn btn--ghost" to="/tech.html">了解更多技术能力</router-link>
        </div>
      </div>
    </section>

    <!-- 行业案例 -->
    <section class="section">
      <div class="ui-container">
        <div class="section-head">
          <span class="kicker">CASES</span>
          <h2>深耕行业，创造价值</h2>
          <p class="sub">解决方案已在多个行业成功落地，为客户带来显著的业务价值与技术赋能</p>
        </div>
        <div class="case-grid">
          <div class="case card" v-for="c in industries" :key="c.name">
            <div class="case__pic">
              <img :src="c.image" :alt="c.name" />
            </div>
            <div class="case__body">
              <h3>{{ c.name }}</h3>
              <p>{{ c.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 精选好物 -->
    <section class="section section--gray">
      <div class="ui-container">
        <div class="section-head">
          <span class="kicker">SELECTION</span>
          <h2>星选好物</h2>
          <p class="sub">科普地图与学习好物，孩子的第一张启蒙地图</p>
        </div>
        <div class="pick-grid">
          <div class="pick card" v-for="m in featuredMall" :key="m.name" @click="goMiniProgram(m)">
            <div class="pick__pic">
              <img :src="m.image" :alt="m.name" />
            </div>
            <h3>{{ m.name }}</h3>
            <div class="pick__ft">
              <b>¥{{ m.price }}</b>
              <span class="pick__go">去选购 →</span>
            </div>
          </div>
        </div>
        <div class="more-line">
          <router-link class="btn btn--primary" to="/mall.html">进入星选商城</router-link>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="cta">
      <div class="ui-container cta__inner">
        <h2>让我们一起打通「硬件—云端—应用—生态」</h2>
        <p>灵活合作：技术授权 · API 对接 · 联合开发 · 产品定制 · 品牌联合推广 · 联合运营</p>
        <router-link class="btn btn--red" to="/about.html">联系我们，共创数智未来</router-link>
      </div>
    </section>

    <footer-el></footer-el>
  </div>
</template>

<script>
  import Header from "../components/header.vue";
  import Footer from "../components/footer.vue";
  import {
    homeStats,
    solutionPillars,
    products,
    serviceCards,
    techPlatforms,
    industries,
    mallItems
  } from "../data/site.js";
  import { launchMiniProgram } from "../utils/miniProgram.js";

  export default {
    components: {
      "header-el": Header,
      "footer-el": Footer
    },
    data() {
      return {
        homeStats,
        solutionPillars,
        products,
        serviceCards,
        techPlatforms,
        industries,
        mallItems,
        heroDevices: [
          { name: "月伴 AI 地球仪", meta: "AI 语音互动", ok: "在线" },
          { name: "小华语音助手", meta: "6 个固件", ok: "在线" },
          { name: "智能彩光灯带", meta: "场景联动", ok: "在线" },
          { name: "车联网终端", meta: "北斗定位", ok: "在线" }
        ]
      };
    },
    computed: {
      featuredProducts() {
        /* 接口数据已按时间倒序，首页默认取前 4 个 */
        return this.products.slice(0, 4);
      },
      featuredMall() {
        return this.mallItems.slice(0, 4);
      }
    },
    methods: {
      goMiniProgram(m) {
        launchMiniProgram(m);
      }
    }
  };
</script>

<style scoped>
  /* ---------- Hero ---------- */
  .hero {
    position: relative;
    background: linear-gradient(135deg, #f0453a 0%, #e8382f 55%, #d32f21 100%);
    color: #fff;
    padding: 84px 0 0;
    overflow: hidden;
  }
  .hero::before,
  .hero::after {
    content: "";
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
  }
  .hero::before { width: 420px; height: 420px; right: -120px; top: -160px; }
  .hero::after { width: 260px; height: 260px; left: -60px; bottom: 40px; }

  .hero__wrap {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 48px;
    padding-bottom: 92px;
  }

  .hero__badge {
    display: inline-block;
    padding: 6px 16px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.16);
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.06em;
  }

  .hero__text h1 {
    margin-top: 20px;
    font-size: 46px;
    font-weight: 700;
    line-height: 1.3;
    letter-spacing: 0.02em;
  }

  .hero__desc {
    margin-top: 18px;
    max-width: 520px;
    font-size: 15px;
    line-height: 1.9;
    color: rgba(255, 255, 255, 0.88);
  }

  .hero__actions {
    display: flex;
    gap: 14px;
    margin-top: 30px;
  }

  .hero__visual { position: relative; flex-shrink: 0; }

  .hero-card {
    background: #fff;
    border-radius: 18px;
    color: #23262d;
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.22);
  }

  .hero-card--main { width: 360px; padding: 20px 22px; }

  .hero-card__hd {
    display: flex;
    align-items: center;
    font-size: 13px;
    font-weight: 600;
    color: #4c515b;
  }

  .hero-card__dot {
    width: 8px;
    height: 8px;
    margin-right: 8px;
    border-radius: 50%;
    background: #2fbf71;
  }

  .hero-card__rows { margin-top: 12px; }

  .hero-row {
    display: flex;
    align-items: center;
    padding: 11px 0;
    border-top: 1px solid #f2f3f5;
    font-size: 13px;
  }
  .hero-row__name { font-weight: 600; width: 132px; }
  .hero-row__meta { flex: 1; color: #8a909b; }
  .hero-row__ok {
    padding: 1px 9px;
    border-radius: 999px;
    background: #e9f8f0;
    color: #1d9e5f;
    font-size: 12px;
  }

  .hero-card--sub {
    position: absolute;
    right: -34px;
    bottom: -46px;
    width: 210px;
    padding: 16px 18px;
  }
  .hero-card--sub b { font-size: 14px; }
  .hero-card--sub p { margin-top: 4px; font-size: 12px; color: #8a909b; }

  .stats-wrap { position: relative; z-index: 3; }
  .stats {
    display: flex;
    padding: 30px 12px;
  }
  .stats__item {
    flex: 1;
    text-align: center;
    border-right: 1px solid #f0e9e8;
  }
  .stats__item:last-child { border-right: none; }
  .stats__item b {
    display: block;
    font-size: 30px;
    font-weight: 700;
    color: #e8382f;
  }
  .stats__item span {
    display: block;
    margin-top: 4px;
    font-size: 13px;
    color: #8a909b;
  }

  /* ---------- 核心业务 ---------- */
  .pillars {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 22px;
  }
  .pillar { overflow: hidden; }
  .pillar__pic {
    aspect-ratio: 1.2;
    background: #fdeceb;
  }
  .pillar__pic img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .pillar__body { padding: 20px 24px 26px; }
  .pillar h3 { font-size: 19px; }
  .pillar p { margin-top: 8px; font-size: 14px; color: #4c515b; line-height: 1.8; }

  /* ---------- 产品速览 ---------- */
  .prod-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
  }
  .prod { position: relative; padding: 18px 18px 20px; }
  .prod .tag { position: absolute; top: 26px; right: 26px; z-index: 1; }
  .prod__pic {
    aspect-ratio: 4 / 3;
    border-radius: 12px;
    background: #fff;
    border: 1px solid #f2f3f5;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .prod__pic img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .prod h3 { margin-top: 14px; font-size: 16px; }
  .prod p {
    margin-top: 6px;
    font-size: 13px;
    color: #8a909b;
    line-height: 1.7;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .prod__fw {
    display: inline-block;
    margin-top: 12px;
    font-size: 12px;
    color: #e8382f;
    font-weight: 600;
  }

  .more-line { text-align: center; margin-top: 34px; }

  /* ---------- 服务双卡 ---------- */
  .svc-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 22px;
  }
  .svc { display: flex; overflow: hidden; }
  .svc__pic {
    flex: none;
    width: 200px;
    background: #fdeceb;
  }
  .svc__pic img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .svc__body { padding: 26px 26px 24px; }
  .svc__en {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.16em;
    color: #e8382f;
  }
  .svc h3 { margin-top: 6px; font-size: 21px; }
  .svc__desc { margin-top: 10px; font-size: 13px; color: #4c515b; line-height: 1.8; }
  .svc .check-list { margin-top: 12px; }
  .svc .check-list li { font-size: 13px; line-height: 1.8; }
  .svc__btn { margin-top: 18px; }

  /* ---------- 技术底座 ---------- */
  .tech-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
  }
  .tech { padding: 18px 18px 22px; }
  .tech__pic {
    height: 140px;
    border-radius: 12px;
    background: #fdeceb;
    overflow: hidden;
  }
  .tech__pic img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .tech h3 { margin-top: 14px; font-size: 17px; }
  .tech p { margin-top: 8px; font-size: 13px; color: #8a909b; line-height: 1.7; }

  /* ---------- 行业案例 ---------- */
  .case-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 22px;
  }
  .case { overflow: hidden; }
  .case__pic {
    height: 190px;
    background: #fdeceb;
  }
  .case__pic img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.3s ease;
  }
  .case:hover .case__pic img { transform: scale(1.04); }
  .case__body { padding: 18px 22px 22px; }
  .case h3 { font-size: 17px; }
  .case p {
    margin-top: 8px;
    font-size: 13px;
    color: #4c515b;
    line-height: 1.8;
  }

  /* ---------- 星选好物 ---------- */
  .pick-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
  }
  .pick { padding: 14px 14px 16px; cursor: pointer; }
  .pick__pic {
    aspect-ratio: 4 / 3;
    border-radius: 12px;
    overflow: hidden;
    background: #f4f5f7;
  }
  .pick__pic img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .pick h3 {
    margin-top: 12px;
    font-size: 14px;
    line-height: 1.5;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .pick__ft {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 10px;
  }
  .pick__ft b { color: #e8382f; font-size: 17px; }
  .pick__go { font-size: 13px; color: #4c515b; }
  .pick__go:hover { color: #e8382f; }

  /* ---------- CTA ---------- */
  .cta { padding: 0 0 84px; }
  .cta__inner {
    background: #23262d;
    border-radius: 24px;
    text-align: center;
    color: #fff;
    padding: 60px 40px;
    position: relative;
    overflow: hidden;
  }
  .cta__inner::before {
    content: "";
    position: absolute;
    width: 420px;
    height: 420px;
    border-radius: 50%;
    background: rgba(232, 56, 47, 0.28);
    filter: blur(90px);
    left: 50%;
    top: -160px;
    transform: translateX(-50%);
  }
  .cta__inner h2,
  .cta__inner p,
  .cta__inner .btn { position: relative; }
  .cta__inner h2 { font-size: 26px; }
  .cta__inner p {
    margin: 12px 0 26px;
    font-size: 14px;
    color: rgba(255, 255, 255, 0.7);
  }

  /* ---------- 响应式（统一断点 1023 / 767） ---------- */
  @media (max-width: 1023px) {
    .hero { padding: 60px 0 0; }
    .hero__wrap {
      flex-direction: column;
      align-items: flex-start;
      gap: 40px;
      padding-bottom: 76px;
    }
    .hero__text h1 { font-size: 34px; }
    .hero__visual { width: 100%; }
    .hero-card--main { width: 100%; }
    .hero-card--sub { right: 0; bottom: -30px; }
    .pillars { grid-template-columns: repeat(2, 1fr); }
    .prod-grid,
    .tech-grid,
    .pick-grid { grid-template-columns: repeat(2, 1fr); }
    .case-grid { grid-template-columns: repeat(2, 1fr); }
    .svc-grid { grid-template-columns: 1fr; }
  }

  @media (max-width: 767px) {
    .hero { padding: 40px 0 0; }
    .hero__wrap { gap: 28px; padding-bottom: 60px; }
    .hero__text h1 { font-size: 26px; }
    .hero__desc { font-size: 14px; }
    .hero__actions { flex-wrap: wrap; }
    .hero-card--sub {
      position: static;
      width: 100%;
      margin-top: 14px;
    }
    .stats { flex-wrap: wrap; padding: 18px 8px; }
    .stats__item { flex: 0 0 50%; padding: 10px 0; border-right: none; }
    .stats__item:nth-child(odd) { border-right: 1px solid #f0e9e8; }
    .stats__item b { font-size: 22px; }
    .pillars,
    .prod-grid,
    .tech-grid,
    .pick-grid,
    .case-grid { grid-template-columns: 1fr; }
    .svc { flex-direction: column; }
    .svc__pic { width: 100%; height: 180px; }
    .cta { padding: 0 0 48px; }
    .cta__inner { padding: 36px 20px; }
    .cta__inner h2 { font-size: 20px; }
  }
</style>
